import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { canEnterApp } from '@helios/authz';
import { useAuth } from '@helios/auth-firebase-web';
import type { NotificationJobClientRow } from '@helios/data-access';
import { useSendWhatsAppReminder } from '../hooks/useSendWhatsAppReminder';
import {
  appointmentReminderReference,
  formatAppointmentReminderParams,
} from '../lib/appointment-reminder';

/* ------------------------------------------------------------------ */
/*  SendWhatsAppReminderAction (PRD.221)                              */
/*                                                                    */
/*  Appointment-context action that sends an APPOINTMENT_REMINDER     */
/*  WhatsApp message via the psendAdhocWhatsApp Cloud Function.       */
/*                                                                    */
/*  - Role-gated: hidden entirely for users without send permission. */
/*  - Opt-in gated: disabled (visible) when the patient has no        */
/*    explicit WhatsApp opt-in or no mobile number on file.           */
/*  - Confirmation dialog before sending.                             */
/*  - Latest reminder status is read from the PowerSync               */
/*    notification_jobs mirror (provider-submit status only).         */
/*                                                                    */
/*  Two presentations:                                                */
/*  - 'menu-item' (default) — a row in a dropdown actions menu.        */
/*  - 'button'    — an outline button for use inside a drawer panel.   */
/* ------------------------------------------------------------------ */

/** Roles allowed to send appointment reminders. */
const ALLOWED_ROLES = ['admin', 'editor'] as const;

export interface SendWhatsAppReminderAppointment {
  id: string;
  patientId: string;
  patientName: string;
  /** "YYYY-MM-DD" */
  date: string;
  /** "HH:mm" */
  startTime: string;
}

export interface SendWhatsAppReminderActionProps {
  appointment: SendWhatsAppReminderAppointment;
  /** Patient's mobile number from patient_contacts (null when none on file). */
  patientPhone: string | null;
  /** True only when the patient has an explicit, non-revoked WhatsApp opt-in. */
  whatsAppOptedIn: boolean;
  /**
   * The most recent notification_jobs row whose client_reference matches this
   * appointment's reminder reference, or null if none exists yet.
   */
  reminderJob: NotificationJobClientRow | null;
  /** Visual presentation. Defaults to 'menu-item'. */
  presentation?: 'menu-item' | 'button';
  /** Called when the confirm dialog opens — lets the parent close its menu. */
  onConfirmOpen?: () => void;
}

type JobStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled' | null;

export function SendWhatsAppReminderAction({
  appointment,
  patientPhone,
  whatsAppOptedIn,
  reminderJob,
  presentation = 'menu-item',
  onConfirmOpen,
}: SendWhatsAppReminderActionProps) {
  const { claims } = useAuth();
  const { enabled, status, error, send, reset } = useSendWhatsAppReminder();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Role gate — hidden entirely for disallowed users ──
  const canSend = canEnterApp(claims, {
    appId: 'whatsapp-appointment-reminder',
    entryAnyOfRoles: [...ALLOWED_ROLES],
  });

  // ── Derived state ──
  // Informational preview only (PRD.254) — the backend builds the actual
  // provider template parameters from notification_templates.metadata.
  const previewParams = useMemo(
    () => formatAppointmentReminderParams(appointment.date, appointment.startTime),
    [appointment.date, appointment.startTime],
  );
  const clientReference = useMemo(
    () => appointmentReminderReference(appointment.id),
    [appointment.id],
  );

  const jobStatus = (reminderJob?.status ?? null) as JobStatus;
  const inFlight = jobStatus === 'pending' || jobStatus === 'sending';
  const alreadySent = jobStatus === 'sent';

  // First blocking reason wins — surfaced as the disabled-trigger hint.
  const blockReason: string | null = !enabled
    ? 'WhatsApp messaging is not configured for this clinic'
    : !patientPhone
      ? 'No mobile number on file for this patient'
      : !whatsAppOptedIn
        ? 'Patient has not opted in to WhatsApp messaging'
        : !previewParams
          ? 'Appointment date/time is unavailable'
          : inFlight
            ? 'A reminder for this appointment is already sending'
            : null;

  const disabled = blockReason !== null;

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const openConfirm = useCallback(() => {
    if (disabled) return;
    reset();
    setConfirmOpen(true);
    onConfirmOpen?.();
  }, [disabled, reset, onConfirmOpen]);

  const handleSend = useCallback(async () => {
    if (status === 'sending') return; // duplicate-click guard
    const result = await send({
      patientId: appointment.patientId,
      appointmentId: appointment.id,
      clientReference,
    });
    if (result) {
      setConfirmOpen(false);
      showToast(
        result.accepted === false
          ? 'Reminder queued — provider did not accept it yet.'
          : `WhatsApp reminder sent to ${appointment.patientName}.`,
      );
    }
    // On failure the modal stays open and surfaces `error` inline for retry.
  }, [
    status,
    send,
    appointment.id,
    appointment.patientId,
    appointment.patientName,
    clientReference,
    showToast,
  ]);

  if (!canSend) return null;

  const actionLabel = alreadySent ? 'Resend WhatsApp Reminder' : 'Send WhatsApp Reminder';
  const statusHint = statusHintFor(jobStatus, reminderJob);
  const hint = blockReason ?? statusHint;

  return (
    <>
      {presentation === 'button' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <Button
            variant="outline"
            size="md"
            disabled={disabled}
            onClick={openConfirm}
            title={blockReason ?? undefined}
          >
            {actionLabel}
          </Button>
          {hint && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {hint}
            </span>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={openConfirm}
          title={blockReason ?? undefined}
          style={{
            ...menuItemStyle,
            color: disabled ? 'var(--text-muted)' : 'var(--text)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={(e) => {
            if (!disabled)
              (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = 'none';
          }}
        >
          <span>{actionLabel}</span>
          {hint && (
            <span
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                marginTop: 2,
              }}
            >
              {hint}
            </span>
          )}
        </button>
      )}

      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Send WhatsApp Reminder"
        description={`This sends an appointment reminder to ${appointment.patientName} over WhatsApp.`}
        maxWidth={420}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button variant="ghost" size="md" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              size="md"
              onClick={handleSend}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending…' : 'Send Reminder'}
            </Button>
          </div>
        }
      >
        <div style={modalBodyStyle}>
          <SummaryRow label="Patient" value={appointment.patientName} />
          <SummaryRow label="WhatsApp" value={patientPhone ?? '—'} />
          <SummaryRow
            label="Appointment"
            value={previewParams ? `${previewParams[0]} at ${previewParams[1]}` : '—'}
          />
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              lineHeight: 'var(--line-height-sm)',
            }}
          >
            Template: <strong>APPOINTMENT_REMINDER</strong>. The patient has opted in to WhatsApp
            messaging.
          </div>

          {status === 'error' && error && (
            <div
              style={{
                padding: 'var(--sp-2) var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: '#fef2f2',
                color: '#dc2626',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>
      </Modal>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'var(--sp-6)',
            right: 'var(--sp-6)',
            zIndex: 1000,
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: '#dcfce7',
            color: '#15803d',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function statusHintFor(status: JobStatus, job: NotificationJobClientRow | null): string | null {
  switch (status) {
    case 'sent':
      return '✓ Reminder sent';
    case 'pending':
    case 'sending':
      return 'Reminder sending…';
    case 'failed':
      return job?.last_error
        ? `Last attempt failed — ${job.last_error}`
        : 'Last attempt failed — tap to retry';
    default:
      return null;
  }
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          fontWeight: 'var(--font-weight-medium)',
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

const menuItemStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'none',
  border: 'none',
  textAlign: 'left',
  fontSize: 'var(--font-size-sm)',
};

/**
 * Modal body padding — the shared Modal component pads only its header and
 * footer, so each caller must pad its own body content.
 */
const modalBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
  padding: 'var(--sp-5, 20px) var(--sp-6, 24px)',
};
